import { memo, useCallback } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import {
  useRejectIssuanceRenewalIdForStaffFromContractedCompanies,
  useRejectLostStickerIDCardRequest,
  useRejectOfficeAccessAuthorization,
  useRejectParkingEntrySticker,
  useRejectPcInOutPermit,
  useRejectPermitToWork,
  useRejectWarehouseInOutPermit,
} from "../../../hooks/permit/mutation";
import i18n from "../../../i18n";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { PERMIT_TYPES } from "../../../constants/common";

interface RejectPermitModalProps {
  type: PERMIT_TYPES;
  permitId: number;
  isOpen: boolean;
  onOpenChange: () => void;
}

const RejectPermitModalSchema = Yup.object({
  comments: Yup.string().required(
    i18n.language === "en" ? "Comments is required." : "التعليقات مطلوبة.",
  ),
});

interface RejectPermitModalData {
  comments: string;
}

const RejectPermitModal = ({
  type,
  permitId,
  isOpen,
  onOpenChange,
}: RejectPermitModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useFormWithSchema(RejectPermitModalSchema);

  const { t } = useTranslation();

  const {
    mutateAsync: RejectPcInOutPermit,
    isPending: isRejectPcInOutPermitLoading,
  } = useRejectPcInOutPermit();

  const {
    mutateAsync: RejectWarehouseInOutPermit,
    isPending: isRejectWarehouseInOutPermitLoading,
  } = useRejectWarehouseInOutPermit();

  const {
    mutateAsync: RejectPermitToWork,
    isPending: isRejectPermitToWorkLoading,
  } = useRejectPermitToWork();

  const {
    mutateAsync: RejectLostStickerIDCardRequest,
    isPending: isRejectLostStickerIDCardRequestLoading,
  } = useRejectLostStickerIDCardRequest();

  const {
    mutateAsync: RejectParkingEntrySticker,
    isPending: isRejectParkingEntryStickerLoading,
  } = useRejectParkingEntrySticker();

  const {
    mutateAsync: RejectOfficeAccessAuthorization,
    isPending: isRejectOfficeAccessAuthorizationLoading,
  } = useRejectOfficeAccessAuthorization();

  const {
    mutateAsync: RejectIssuanceRenewalIdForStaffFromContractedCompanies,
    isPending: isRejectIssuanceRenewalIdForStaffFromContractedCompaniesLoading,
  } = useRejectIssuanceRenewalIdForStaffFromContractedCompanies();

  const navigate = useNavigate();

  const handleRejectAppointment = useCallback(
    async (data: RejectPermitModalData) => {
      try {
        if (type === PERMIT_TYPES.PC_IN_OUT_PERMIT) {
          const body = {
            n_LaptopPermitID: permitId,
            s_Comments: data.comments,
          };

          await RejectPcInOutPermit(body);
        } else if (type === PERMIT_TYPES.WAREHOUSE_IN_OUT_PERMIT) {
          const body = {
            n_PermitWarehouseID: permitId,
            s_Comments: data.comments,
          };

          await RejectWarehouseInOutPermit(body);
        } else if (type === PERMIT_TYPES.PERMIT_TO_WORK) {
          const body = {
            n_PermitToWorkID: permitId,
            s_Comments: data.comments,
          };

          await RejectPermitToWork(body);
        } else if (type === PERMIT_TYPES.LOST_VEHICLE_STICKER_ID_CARD) {
          const body = {
            lostStickerRequestId: permitId,
            comments: data.comments,
          };
          await RejectLostStickerIDCardRequest(body);
        } else if (type === PERMIT_TYPES.PARKING_STICKER_REQUEST) {
          const body = {
            parkingStickerRequestId: permitId,
            comments: data.comments,
          };
          await RejectParkingEntrySticker(body);
        } else if (type === PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION) {
          const body = {
            officeAccessAuthorisationRequestId: permitId,
            comments: data.comments,
          };
          await RejectOfficeAccessAuthorization(body);
        } else if (type === PERMIT_TYPES.ISSUANCE_RENEWAL_ID) {
          const body = {
            contractorStaffIdRequestId: permitId,
            comments: data.comments,
          };
          await RejectIssuanceRenewalIdForStaffFromContractedCompanies(body);
        }

        toast.success(t("permitRejected"));

        setTimeout(() => {
          navigate(APP_ROUTES.APPROVE_PERMIT_REQUEST);
        }, 700);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onOpenChange();
      }
    },
    [
      type,
      t,
      permitId,
      RejectPcInOutPermit,
      RejectWarehouseInOutPermit,
      RejectPermitToWork,
      RejectLostStickerIDCardRequest,
      RejectParkingEntrySticker,
      RejectOfficeAccessAuthorization,
      RejectIssuanceRenewalIdForStaffFromContractedCompanies,
      navigate,
      onOpenChange,
    ],
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="md:!max-w-2xl"
    >
      <form
        onSubmit={handleSubmit(handleRejectAppointment)}
        className="flex flex-col gap-5 justify-center items-center w-full"
      >
        <p className="text-lg font-medium">{t("confirmRejection")}</p>

        <div className="w-3/4">
          <Controller
            name="comments"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("comments")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
              />
            )}
          />
        </div>

        <div className="flex gap-2 items-center">
          <Button
            disabled={
              isRejectPcInOutPermitLoading ||
              isRejectWarehouseInOutPermitLoading ||
              isRejectPermitToWorkLoading ||
              isRejectLostStickerIDCardRequestLoading ||
              isRejectParkingEntryStickerLoading ||
              isRejectOfficeAccessAuthorizationLoading ||
              isRejectIssuanceRenewalIdForStaffFromContractedCompaniesLoading
            }
            isLoading={
              isRejectPcInOutPermitLoading ||
              isRejectWarehouseInOutPermitLoading ||
              isRejectPermitToWorkLoading ||
              isRejectLostStickerIDCardRequestLoading ||
              isRejectParkingEntryStickerLoading ||
              isRejectOfficeAccessAuthorizationLoading ||
              isRejectIssuanceRenewalIdForStaffFromContractedCompaniesLoading
            }
            className=" bg-red-600 hover:bg-red-700"
          >
            {t("reject")}
          </Button>

          <Button
            onClick={onOpenChange}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
          >
            {t("close")}
          </Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default memo(RejectPermitModal);
